(ns practitest.frontend.landing.project-list
  (:require [practitest.frontend.common.dom :as dom]
            [practitest.frontend.common.item-card :refer [item-card]]
            [practitest.frontend.common.i18n :refer [get-translation]]
            [practitest.frontend.common.format-utils :refer [format-time-ago]]
            [practitest.frontend.components.icons :as icons]
            [practitest.frontend.components2.pagination :refer [pagination-view]]
            [practitest.frontend.components2.dropdown :refer [dropdown-button dropdown-menu-item]]
            [practitest.frontend.landing.subs :as landing-subs]
            [practitest.frontend.subs :as subs]
            [practitest.frontend.common.utils :refer [email?]]
            [re-frame.core :as re-frame]
            [clojure.string :as str]))

(defn has-project-permission?
  "Checks if user has a specific permission for a given project"
  [user-projects project-id permission]
  (let [user-project (first (filter #(= (:id (:project %)) project-id) user-projects))]
    (contains? (:permissions user-project) permission)))

(defn counts-description [project]
  (let [counts (remove nil?
                 [(when-let [c (:requirements_count project)]
                    (str c " " (get-translation "requirement.plural" "Requirements")))
                  (when-let [c (:tests_count project)]
                    (str c " " (get-translation "test.plural" "Tests")))
                  (when-let [c (:testsets_count project)]
                    (str c " " (get-translation "testset.plural" "Test Sets")))
                  (when-let [c (:issues_count project)]
                    (str c " " (get-translation "issue.plural" "Issues")))])]
    (when (seq counts)
      (str/join " · " counts))))

(defn project-title-dropdown [project-name base-url has-permission?]
  [dropdown-button
   {:text project-name
    :class "pt-item-card-title"
    :tertiary? true
    :on-click #(dom/stop-propagation %)}
   {:placement "bottom-start"}
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.dashboards" "Dashboards")
                        :icon [icons/mux-dashboards]
                        :on-click #(dom/navigate! (str base-url "/summary"))}]
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.reports" "Reports")
                        :icon [icons/mux-reports]
                        :disabled? (not (has-permission? "reports"))
                        :on-click #(dom/navigate! (str base-url "/reports"))}]
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.milestones" "Milestones")
                        :icon [icons/mux-milestones]
                        :disabled? (not (has-permission? "milestones_view"))
                        :on-click #(dom/navigate! (str base-url "/milestones"))}]
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.requirements" "Requirements")
                        :icon [icons/mux-requirements]
                        :disabled? (not (has-permission? "requirements_view"))
                        :on-click #(dom/navigate! (str base-url "/requirements"))}]
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.test-library" "Test Library")
                        :icon [icons/mux-test-library]
                        :disabled? (not (has-permission? "tests_runs_view"))
                        :on-click #(dom/navigate! (str base-url "/tests"))}]
   [dropdown-menu-item {:text (get-translation "testset.plural" "Test Sets")
                        :icon [icons/mux-testsets-runs]
                        :disabled? (not (has-permission? "tests_runs_view"))
                        :on-click #(dom/navigate! (str base-url "/sets"))}]
   [dropdown-menu-item {:text (get-translation "navigation.project-menu.issues" "Issues")
                        :icon [icons/mux-issues]
                        :disabled? (not (has-permission? "issues_view"))
                        :on-click #(dom/navigate! (str base-url "/issues"))}]])

(defn project-card [project]
  (let [project-id (:id project)
        base-url (str "/p/" project-id)
        user-projects @(re-frame/subscribe [::subs/user-projects])
        has-permission? (partial has-project-permission? user-projects project-id)]
    [item-card
     {:title [project-title-dropdown (:name project) base-url has-permission?]
      :description (or (not-empty (:description project)) (counts-description project))
      :footer [:div
               [icons/mux-calendar-manual]
               [:span
                (get-translation "landing.project-card.my-last-activity" "My last activity") ": "
                (format-time-ago (:last_activity project))]]}]))

(defn pagination-controls []
  (let [current-page @(re-frame/subscribe [:landing/projects-page])
        total-projects @(re-frame/subscribe [:landing/total-projects])]
    [pagination-view
     {:on-change #(re-frame/dispatch [:landing/change-projects-page %])
      :current-page current-page
      :total-count total-projects
      :per-page landing-subs/projects-per-page
      :max-buttons 0
      :always-show-nav? true}]))

(def modules
  [{:key "dashboards"    :text "Dashboards"     :icon icons/mux-dashboards      :path "/summary"      :permission nil}
   {:key "reports"       :text "Reports"        :icon icons/mux-reports         :path "/reports"      :permission "reports"}
   {:key "milestones"    :text "Milestones"     :icon icons/mux-milestones      :path "/milestones"   :permission "milestones_view"}
   {:key "requirements"  :text "Requirements"   :icon icons/mux-requirements    :path "/requirements" :permission "requirements_view"}
   {:key "test-library"  :text "Test Library"   :icon icons/mux-test-library    :path "/tests"        :permission "tests_runs_view"}
   {:key "test-sets"     :text "Test Sets"      :icon icons/mux-testsets-runs   :path "/sets"         :permission "tests_runs_view"}
   {:key "issues"        :text "Issues"         :icon icons/mux-issues          :path "/issues"       :permission "issues_view"}])

(defn module-card [{:keys [text icon path base-url]}]
  [item-card
   {:title text
    :icon [icon]
    :class "pt-module-card clickable"
    :on-click #(dom/navigate! (str base-url path))}])

(defn module-cards [project user-name]
  (let [project-id (:id project)
        base-url (str "/p/" project-id)
        user-projects @(re-frame/subscribe [::subs/user-projects])
        has-permission? (partial has-project-permission? user-projects project-id)
        permitted-modules (filter (fn [{:keys [permission]}]
                                    (or (nil? permission)
                                        (has-permission? permission)))
                                  modules)]
    [:div.pt-landing-projects-section
     [:div.pt-landing-projects-header
      [:div
       [:h2.pt-container-card-title [icons/mux-hand] (get-translation "landing.title" "Hello") (when-not (email? user-name) (str " " user-name))]
       [:p.pt-container-card-subtitle (get-translation "landing.my-modules.subtitle" "Your Project's Modules")]]]
     [:div.pt-projects-grid-container
      [:div.pt-modules-grid
       (for [module permitted-modules]
         ^{:key (:key module)}
         [module-card (assoc module :base-url base-url)])]]]))

(defn project-list []
  (let [projects @(re-frame/subscribe [:landing/projects])
        total-projects @(re-frame/subscribe [:landing/total-projects])
        user @(re-frame/subscribe [::subs/current-user])
        user-name (:name user)]
    (if (= total-projects 1)
      [module-cards (first projects) user-name]
      [:div.pt-landing-projects-section
       [:div.pt-landing-projects-header
        [:div
         [:h2.pt-container-card-title [icons/mux-hand] (get-translation "landing.title" "Hello") (when-not (email? user-name) (str " " user-name))]
         [:p.pt-container-card-subtitle (get-translation "landing.my-projects.subtitle" "Your recently used projects (Top 60)")]]
        [pagination-controls]]
       [:div.pt-projects-grid-container
        [:div.pt-projects-grid
         (for [project projects]
           ^{:key (:id project)}
           [project-card project])]]])))

